// src/pages/TradeHistory.js — Admin trade history (POST /admin/trade-history)
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageTransition from "../components/PageTransition";
import toast from "react-hot-toast";
import { fetchTradeHistory, fetchTradeListEntities } from "../services/tradeHistoryApi";
import { useAuth } from "../context/AuthContext";
import {
    FaSyncAlt,
    FaChevronLeft,
    FaChevronRight,
    FaHistory,
    FaCalendarAlt,
    FaArrowUp,
    FaArrowDown,
    FaCheckCircle,
    FaTimesCircle,
    FaQuestionCircle,
    FaUndo,
} from "react-icons/fa";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

const PERIOD_OPTIONS = [
    { value: "", label: "All Periods" },
    { value: "30s", label: "30s" },
    { value: "1m", label: "1m" },
    { value: "3m", label: "3m" },
    { value: "5m", label: "5m" },
    { value: "10m", label: "10m" },
    { value: "1h", label: "1h" },
];

const STATUS_OPTIONS = [
    { value: "", label: "All" },
    { value: "0", label: "Pending" },
    { value: "1", label: "Win" },
    { value: "2", label: "Loss" },
];

function safeText(v) {
    return v === null || v === undefined || v === "" ? "—" : String(v);
}

function toYMD(d = new Date()) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function clampToMaxDate(ymd, maxYmd) {
    if (!ymd) return maxYmd;
    return ymd > maxYmd ? maxYmd : ymd;
}

function nfmt(v, digits = 2) {
    const num = Number(v);
    if (!Number.isFinite(num)) return safeText(v);
    return new Intl.NumberFormat("en-IN", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    }).format(num);
}

function formatDateTime(v) {
    if (!v) return "—";
    const dt = new Date(v);
    if (!Number.isFinite(dt.getTime())) return safeText(v);
    return dt.toLocaleString("en-IN", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
    });
}

function validateDateRange(from, to, maxYmd) {
    if (!from || !to) return "Please select both From Date and To Date";
    if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) {
        return "Dates must be in YYYY-MM-DD format";
    }
    if (from > maxYmd) return "From Date cannot be in the future";
    if (to > maxYmd) return "To Date cannot be in the future";
    if (from > to) return "From Date cannot be greater than To Date";
    return null;
}

function tradeStatusMeta(status) {
    const s = String(status ?? "");
    if (s === "0") return { label: "Pending", badge: "bg-warning text-dark", icon: <FaQuestionCircle /> };
    if (s === "1") return { label: "Win", badge: "bg-success", icon: <FaCheckCircle /> };
    if (s === "2") return { label: "Loss", badge: "bg-danger", icon: <FaTimesCircle /> };
    return { label: "Unknown", badge: "bg-secondary", icon: null };
}

function StatusBadge({ status }) {
    const meta = tradeStatusMeta(status);
    return (
        <span
            className={`badge ${meta.badge} d-inline-flex align-items-center justify-content-center`}
            style={{ fontSize: "0.85rem", width: 28, height: 28 }}
            title={meta.label}
        >
            {meta.icon || "—"}
        </span>
    );
}

function SideBadge({ side }) {
    const s = String(side || "").toLowerCase();
    if (s === "up") {
        return (
            <span className="badge bg-success px-2 py-1 d-inline-flex align-items-center" style={{ fontSize: "0.75rem" }}>
                <FaArrowUp className="me-1" size={10} /> Up
            </span>
        );
    }
    if (s === "down") {
        return (
            <span className="badge bg-danger px-2 py-1 d-inline-flex align-items-center" style={{ fontSize: "0.75rem" }}>
                <FaArrowDown className="me-1" size={10} /> Down
            </span>
        );
    }
    return (
        <span className="badge bg-secondary px-2 py-1" style={{ fontSize: "0.75rem" }}>
            {safeText(side)}
        </span>
    );
}

function ProfitCell({ profit }) {
    const num = Number(profit);
    if (!Number.isFinite(num)) return <span className="text-muted">—</span>;
    const cls = num > 0 ? "text-success" : num < 0 ? "text-danger" : "text-muted";
    const sign = num > 0 ? "+" : "";
    return (
        <span className={`fw-semibold ${cls}`}>
            {sign}
            {nfmt(num, 2)}
        </span>
    );
}

function LoadingRow({ cols = 10 }) {
    return (
        <tr>
            {Array.from({ length: cols }).map((_, i) => (
                <td key={i} className="px-3 py-3">
                    <div className="placeholder-glow">
                        <span className="placeholder col-10" />
                    </div>
                </td>
            ))}
        </tr>
    );
}

function PaginationBar({ loading, pageNo, totalPages, hasMore, total, limit, onPrev, onNext, onJump }) {
    const maxPage = Math.max(1, Number(totalPages) || 1);
    const [jumpInput, setJumpInput] = useState(String(pageNo));

    useEffect(() => {
        setJumpInput(String(pageNo));
    }, [pageNo]);

    const submitJump = (e) => {
        e?.preventDefault?.();
        if (loading || maxPage <= 1) return;

        const raw = jumpInput.trim();
        if (!raw) {
            toast.error("Enter a page number");
            setJumpInput(String(pageNo));
            return;
        }

        const n = Number.parseInt(raw, 10);
        if (!Number.isFinite(n) || n < 1 || n > maxPage) {
            toast.error(`Page must be between 1 and ${maxPage}`);
            setJumpInput(String(pageNo));
            return;
        }

        if (n !== pageNo) onJump(n);
    };

    return (
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div className="text-muted small">
                Total <span className="fw-semibold text-dark">{nfmt(total, 0)}</span>
                {" · "}
                Page <span className="fw-semibold text-dark">{pageNo}</span> of{" "}
                <span className="fw-semibold text-dark">{maxPage}</span>
                {limit ? (
                    <>
                        {" · "}
                        <span className="text-muted">{limit} per page</span>
                    </>
                ) : null}
            </div>
            <div className="d-flex align-items-center gap-2">
                <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm px-3 py-1 d-inline-flex align-items-center gap-2"
                    onClick={onPrev}
                    disabled={loading || pageNo <= 1}
                >
                    <FaChevronLeft /> Prev
                </button>
                <form className="d-flex align-items-center gap-2" onSubmit={submitJump}>
                    <span className="text-muted small">Go to</span>
                    <input
                        type="number"
                        className="form-control form-control-sm"
                        style={{ width: 72 }}
                        min={1}
                        max={maxPage}
                        value={jumpInput}
                        onChange={(e) => setJumpInput(e.target.value)}
                        disabled={loading || maxPage <= 1}
                        aria-label="Page number"
                        title={`Enter 1–${maxPage} and press Enter`}
                    />
                </form>
                <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm px-3 py-1 d-inline-flex align-items-center gap-2"
                    onClick={onNext}
                    disabled={loading || pageNo >= maxPage || !hasMore}
                >
                    Next <FaChevronRight />
                </button>
            </div>
        </div>
    );
}

export default function TradeHistory() {
    const { logout } = useAuth();
    const today = useMemo(() => toYMD(new Date()), []);

    const [fromDate, setFromDate] = useState(today);
    const [toDate, setToDate] = useState(today);
    const [period, setPeriod] = useState("");
    const [entityId, setEntityId] = useState("");
    const [status, setStatus] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const [pageNo, setPageNo] = useState(1);
    const [loading, setLoading] = useState(true);
    const [list, setList] = useState([]);
    const [meta, setMeta] = useState({
        total: 0,
        page_no: 1,
        limit: PAGE_SIZE,
        has_more: false,
        total_page: 1,
    });
    const [entities, setEntities] = useState([]);
    const [entitiesLoading, setEntitiesLoading] = useState(true);

    const fetchIdRef = useRef(0);
    const lastDateErrorRef = useRef("");

    const totalPages = useMemo(() => {
        const apiPages = Number(meta?.total_page || 0);
        if (apiPages > 0) return apiPages;
        const total = Number(meta?.total || 0);
        const limit = Number(meta?.limit || PAGE_SIZE);
        if (!total) return 1;
        return Math.max(1, Math.ceil(total / limit));
    }, [meta?.total_page, meta?.total, meta?.limit]);

    const resetPage = useCallback(() => setPageNo(1), []);

    const onFromDateChange = useCallback(
        (value) => {
            const next = clampToMaxDate(value, today);
            setFromDate(next);
            if (toDate && next > toDate) setToDate(next);
            resetPage();
        },
        [today, toDate, resetPage]
    );

    const onToDateChange = useCallback(
        (value) => {
            const next = clampToMaxDate(value, today);
            setToDate(next);
            if (fromDate && next < fromDate) setFromDate(next);
            resetPage();
        },
        [today, fromDate, resetPage]
    );

    const loadEntities = useCallback(async () => {
        setEntitiesLoading(true);
        try {
            const token = localStorage.getItem("token") || "";
            if (!token) return;
            const data = await fetchTradeListEntities(token);
            if (data?.error === "session expired") {
                logout();
                return;
            }
            if (data?.error) {
                setEntities([]);
                return;
            }
            setEntities(Array.isArray(data?.data) ? data.data : []);
        } catch (err) {
            console.error(err);
            setEntities([]);
        } finally {
            setEntitiesLoading(false);
        }
    }, [logout]);

    useEffect(() => {
        loadEntities();
    }, [loadEntities]);

    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedSearch(searchInput.trim());
            setPageNo(1);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [searchInput]);

    const fetchHistory = useCallback(
        async (page, filters) => {
            const dateErr = validateDateRange(filters.from_date, filters.to_date, today);
            if (dateErr) {
                if (lastDateErrorRef.current !== dateErr) {
                    lastDateErrorRef.current = dateErr;
                    toast.error(dateErr);
                }
                setList([]);
                setMeta({ total: 0, page_no: 1, limit: PAGE_SIZE, has_more: false, total_page: 1 });
                setLoading(false);
                return;
            }
            lastDateErrorRef.current = "";

            const token = localStorage.getItem("token") || "";
            if (!token) {
                toast.error("Token not found. Please login again.");
                logout();
                return;
            }

            const reqId = ++fetchIdRef.current;
            setLoading(true);
            try {
                const data = await fetchTradeHistory({
                    token,
                    from_date: filters.from_date,
                    to_date: filters.to_date,
                    period: filters.period || "",
                    entity_id: filters.entity_id || "",
                    search: filters.search || "",
                    status: filters.status || "",
                    page_no: page,
                    limit: PAGE_SIZE,
                });

                if (reqId !== fetchIdRef.current) return;

                if (data?.error === "session expired") {
                    logout();
                    return;
                }
                if (data?.error) {
                    const msg =
                        typeof data?.error === "string"
                            ? data.error
                            : data?.message || data?.msg || "Failed to load trade history";
                    toast.error(msg);
                    setList([]);
                    setMeta({ total: 0, page_no: page, limit: PAGE_SIZE, has_more: false, total_page: 1 });
                    return;
                }

                const payload = data?.data || {};
                setList(Array.isArray(payload?.list) ? payload.list : []);
                const m = payload?.meta || {};
                setMeta({
                    total: Number(m?.total || 0),
                    page_no: Number(m?.page_no || page),
                    limit: Number(m?.limit || PAGE_SIZE),
                    has_more: !!m?.has_more,
                    total_page: Math.max(1, Number(m?.total_page || 1)),
                });
            } catch (err) {
                if (reqId !== fetchIdRef.current) return;
                console.error(err);
                toast.error("Failed to load trade history");
                setList([]);
                setMeta({ total: 0, page_no: page, limit: PAGE_SIZE, has_more: false, total_page: 1 });
            } finally {
                if (reqId === fetchIdRef.current) setLoading(false);
            }
        },
        [logout, today]
    );

    useEffect(() => {
        fetchHistory(pageNo, {
            from_date: fromDate,
            to_date: toDate,
            period,
            entity_id: entityId,
            status,
            search: debouncedSearch,
        });
    }, [pageNo, fromDate, toDate, period, entityId, status, debouncedSearch, fetchHistory]);

    const onResetForm = () => {
        const now = toYMD(new Date());
        lastDateErrorRef.current = "";
        setFromDate(now);
        setToDate(now);
        setPeriod("");
        setEntityId("");
        setStatus("");
        setSearchInput("");
        setDebouncedSearch("");
        setPageNo(1);
    };

    const onRefresh = () => {
        fetchHistory(pageNo, {
            from_date: fromDate,
            to_date: toDate,
            period,
            entity_id: entityId,
            status,
            search: debouncedSearch,
        });
    };

    const onPrev = () => setPageNo((p) => Math.max(1, p - 1));
    const onNext = () => {
        if (meta?.has_more || pageNo < totalPages) setPageNo((p) => p + 1);
    };
    const onJump = (p) => setPageNo(Math.min(totalPages, Math.max(1, p)));

    const serial = (idx) => (Math.max(1, pageNo) - 1) * Number(meta?.limit || PAGE_SIZE) + idx + 1;

    const entityOptions = useMemo(() => {
        const seen = new Set();
        const opts = [];
        entities.forEach((e) => {
            const id = safeText(e?.entity_id);
            if (id === "—" || seen.has(id)) return;
            seen.add(id);
            opts.push({ id, name: safeText(e?.entity_name) });
        });
        return opts.sort((a, b) => a.name.localeCompare(b.name));
    }, [entities]);

    return (
        <PageTransition>
            <div className="container-fluid py-3">
                <div className="row mb-3">
                    <div className="col">
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                            <div>
                                <h1 className="h3 mb-1 d-flex align-items-center gap-2">
                                    <FaHistory className="text-primary" />
                                    Trade History
                                </h1>
                                <p className="text-muted mb-0 small">
                                    Total records: <strong>{nfmt(meta.total, 0)}</strong>
                                </p>
                            </div>
                            <div className="d-flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary d-inline-flex align-items-center gap-2"
                                    onClick={onResetForm}
                                    disabled={loading}
                                    title="Reset filters to today"
                                >
                                    <FaUndo />
                                    Reset
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-primary d-inline-flex align-items-center gap-2"
                                    onClick={onRefresh}
                                    disabled={loading}
                                >
                                    <FaSyncAlt className={loading ? "tb-spin" : ""} />
                                    {loading ? "Loading..." : "Refresh"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 14 }}>
                    <div className="card-body">
                        <div className="row g-3 align-items-end">
                            <div className="col-12 col-md-6 col-lg-3">
                                <label className="form-label small text-muted mb-1">
                                    <FaCalendarAlt className="me-1" />
                                    From Date
                                </label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={fromDate}
                                    max={toDate && toDate <= today ? toDate : today}
                                    onChange={(e) => onFromDateChange(e.target.value)}
                                />
                            </div>
                            <div className="col-12 col-md-6 col-lg-3">
                                <label className="form-label small text-muted mb-1">
                                    <FaCalendarAlt className="me-1" />
                                    To Date
                                </label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={toDate}
                                    min={fromDate || undefined}
                                    max={today}
                                    onChange={(e) => onToDateChange(e.target.value)}
                                />
                            </div>
                            <div className="col-12 col-md-6 col-lg-2">
                                <label className="form-label small text-muted mb-1">Period</label>
                                <select
                                    className="form-select"
                                    value={period}
                                    onChange={(e) => {
                                        setPeriod(e.target.value);
                                        resetPage();
                                    }}
                                >
                                    {PERIOD_OPTIONS.map((o) => (
                                        <option key={o.value || "all"} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-12 col-md-6 col-lg-2">
                                <label className="form-label small text-muted mb-1">Entity</label>
                                <select
                                    className="form-select"
                                    value={entityId}
                                    onChange={(e) => {
                                        setEntityId(e.target.value);
                                        resetPage();
                                    }}
                                    disabled={entitiesLoading}
                                >
                                    <option value="">All Entities</option>
                                    {entityOptions.map((e) => (
                                        <option key={e.id} value={e.id}>
                                            {e.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-12 col-md-6 col-lg-2">
                                <label className="form-label small text-muted mb-1">Status</label>
                                <select
                                    className="form-select"
                                    value={status}
                                    onChange={(e) => {
                                        setStatus(e.target.value);
                                        resetPage();
                                    }}
                                >
                                    {STATUS_OPTIONS.map((o) => (
                                        <option key={o.value || "all"} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-12">
                                <label className="form-label small text-muted mb-1">Search user</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Name, email, mobile, user id, country code…"
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card border-0 shadow-sm" style={{ borderRadius: 14 }}>
                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0 trade-history-table">
                                <thead className="table-light">
                                    <tr>
                                        <th style={{ width: "4%" }}>#</th>
                                        <th style={{ width: "14%" }}>Date</th>
                                        <th style={{ width: "16%" }}>User</th>
                                        <th style={{ width: "14%" }}>Entity</th>
                                        <th style={{ width: "7%" }}>Period</th>
                                        <th style={{ width: "7%" }}>Side</th>
                                        <th style={{ width: "10%" }} className="text-end">
                                            Stake
                                        </th>
                                        <th style={{ width: "10%" }} className="text-end">
                                            Payout
                                        </th>
                                        <th style={{ width: "9%" }} className="text-end">
                                            Profit
                                        </th>
                                        <th style={{ width: "9%" }}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        Array.from({ length: 6 }).map((_, i) => <LoadingRow key={i} cols={10} />)
                                    ) : list.length === 0 ? (
                                        <tr>
                                            <td colSpan={10} className="text-center py-5 text-muted">
                                                <FaHistory className="mb-2" size={22} />
                                                <div className="fw-semibold">No trades found</div>
                                                <div className="small">Try adjusting filters or date range</div>
                                            </td>
                                        </tr>
                                    ) : (
                                        list.map((r, idx) => {
                                            const u = r?.user || {};
                                            const e = r?.entity || {};
                                            const cc = u?.country_code;
                                            const mobile =
                                                safeText(u?.mobile) === "—"
                                                    ? "—"
                                                    : `+${cc ?? ""} ${u.mobile}`.trim();
                                            return (
                                                <tr key={`${r?.trade_id || ""}_${idx}`}>
                                                    <td className="fw-semibold text-muted">{serial(idx)}</td>
                                                    <td className="small">{formatDateTime(r?.create_date)}</td>
                                                    <td>
                                                        <div className="fw-semibold">{safeText(u?.name)}</div>
                                                        <small className="text-muted">ID {safeText(u?.user_id)}</small>
                                                        <div className="text-muted small">{mobile}</div>
                                                        {safeText(u?.email) !== "—" ? (
                                                            <div className="text-muted small" style={{ wordBreak: "break-word" }}>
                                                                {u.email}
                                                            </div>
                                                        ) : null}
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            {e?.image ? (
                                                                <img
                                                                    src={e.image}
                                                                    alt=""
                                                                    width={28}
                                                                    height={28}
                                                                    className="rounded-circle border"
                                                                    style={{ objectFit: "cover" }}
                                                                />
                                                            ) : null}
                                                            <div>
                                                                <div className="fw-semibold small">
                                                                    {safeText(e?.entity_name)}
                                                                </div>
                                                                <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                                                                    {safeText(r?.entity_id)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="badge bg-light text-dark border">
                                                            {safeText(r?.period)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <SideBadge side={r?.side} />
                                                    </td>
                                                    <td className="text-end fw-semibold">{nfmt(r?.trade_amount, 2)}</td>
                                                    <td className="text-end fw-semibold">{nfmt(r?.win_amount, 2)}</td>
                                                    <td className="text-end">
                                                        <ProfitCell profit={r?.profit} />
                                                    </td>
                                                    <td>
                                                        <StatusBadge status={r?.status} />
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {!loading && list.length > 0 && (
                            <div className="card-footer bg-white border-top px-3 py-3">
                                <PaginationBar
                                    loading={loading}
                                    pageNo={pageNo}
                                    totalPages={totalPages}
                                    hasMore={meta?.has_more}
                                    total={meta?.total}
                                    limit={meta?.limit}
                                    onPrev={onPrev}
                                    onNext={onNext}
                                    onJump={onJump}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <style>{`
                .trade-history-table th {
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.04em;
                    white-space: nowrap;
                }
                .trade-history-table td {
                    font-size: 0.875rem;
                    vertical-align: middle;
                }
            `}</style>
        </PageTransition>
    );
}
